import { useState } from 'react';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  ChevronDown,
  ClipboardList,
  ExternalLink,
  Info,
  Lock,
} from 'lucide-react';
import type { CareTeamPatientCard } from '../../services/api/types.gen';
import { useInbox } from '../../hooks/chat/useChatQueries';
import { useStore } from '../../store';
import {
  disciplineName,
  disciplineSortIndex,
} from '../../constants/disciplines';
import {
  formatDateForDisplay,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import { statusDisplayLabel } from '../../utils/episodeStatus';
import { patientEpisodePath } from '../../utils/navigation';
import { MOTION } from './chatMotion';
import { fitMiddle } from './fitText';
import { FittedText } from './FittedText';
import { UnreadPill } from './UnreadPill';

/** `9/22/2026 – 11/20/2026`; the short form drops the shared year from the start. */
// eslint-disable-next-line react-refresh/only-export-components -- the range formatter sits beside the one component that uses it
export function formatEpisodeRange(
  start: string,
  end: string,
  short: boolean
): string {
  const from = formatDateToMMDDYYYY(start);
  const to = formatDateToMMDDYYYY(end);
  if (short && start.slice(0, 4) === end.slice(0, 4)) {
    return `${from.slice(0, from.lastIndexOf('/'))} – ${to}`;
  }
  return `${from} – ${to}`;
}

const DOT_CLASS: Record<string, string> = {
  active: 'bg-green-600',
  interrupted: 'bg-amber-600',
};

interface PatientBandProps {
  card: CareTeamPatientCard;
  patientId: string;
  episodeId: string;
  channelId: string;
  variant: 'window' | 'wide' | 'phone';
  memberCount?: number;
  onShowMembers?: () => void;
}

/** The patient a care-team chat is about, under the chat header (EMR-2547). */
export function PatientBand({
  card,
  patientId,
  episodeId,
  channelId,
  variant,
  memberCount,
  onShowMembers,
}: PatientBandProps) {
  const [open, setOpen] = useState(false);
  const phone = variant === 'phone';
  const wide = variant === 'wide';
  const { data: inbox } = useInbox();
  // The header card is a snapshot; the live inbox row keeps the pills current.
  const options = card.episodes.map(option => ({
    ...option,
    unread_count:
      inbox?.find(r => r.channel_id === option.channel_id)?.unread_count ??
      option.unread_count,
  }));
  const fullRange = formatEpisodeRange(
    card.episode_start_date,
    card.episode_end_date,
    false
  );
  // The closed row drops the years; screen readers and the menu keep them.
  const noYear = (iso: string) => {
    const d = formatDateToMMDDYYYY(iso);
    return d.slice(0, d.lastIndexOf('/'));
  };
  const shortRange = `${noYear(card.episode_start_date)} – ${noYear(card.episode_end_date)}`;

  // The closed row stays quiet: the status is a dot, its word in the title
  // and for screen readers; the menu and the details show the word.
  const status = (
    <span
      title={statusDisplayLabel(card.episode_status)}
      className='flex shrink-0 items-center'
    >
      <span
        aria-hidden='true'
        className={`h-1.5 w-1.5 rounded-full ${DOT_CLASS[card.episode_status] ?? 'bg-slate-400'}`}
      />
      <span className='sr-only'>
        Status {statusDisplayLabel(card.episode_status)}
      </span>
    </span>
  );

  const rangeText = (
    <>
      <span className='sr-only'>Episode {fullRange}</span>
      <span
        aria-hidden='true'
        className={
          phone
            ? 'whitespace-nowrap text-sm tabular-nums'
            : 'whitespace-nowrap text-[11px] tabular-nums'
        }
      >
        {shortRange}
      </span>
    </>
  );

  const careTeam = [...card.care_team_disciplines].sort(
    (a, b) => disciplineSortIndex(a) - disciplineSortIndex(b)
  );
  const caseManager = card.case_manager_name
    ? card.case_manager_credentials
      ? `${card.case_manager_name}, ${card.case_manager_credentials}`
      : card.case_manager_name
    : null;
  const clinicalManager = card.clinical_manager_name
    ? card.clinical_manager_credentials
      ? `${card.clinical_manager_name}, ${card.clinical_manager_credentials}`
      : card.clinical_manager_name
    : 'Unassigned';
  const nextVisit = card.next_visit_date
    ? card.next_visit_discipline
      ? `${formatDateForDisplay(card.next_visit_date)} · ${disciplineName(card.next_visit_discipline)}`
      : formatDateForDisplay(card.next_visit_date)
    : null;

  const dtClass = phone
    ? 'w-24 shrink-0 text-xs leading-6 text-muted-foreground'
    : 'w-20 shrink-0 text-[11px] leading-5 text-muted-foreground';
  const ddClass = phone
    ? 'min-w-0 flex-1 truncate text-sm leading-6'
    : 'min-w-0 flex-1 truncate text-xs leading-5';
  const row = 'flex min-w-0 items-center gap-1.5';

  const link = (
    <a
      href={patientEpisodePath(patientId, episodeId)}
      target='_blank'
      rel='noopener noreferrer'
      title={
        card.mrn
          ? 'Open chart (new tab)'
          : 'No MRN on file. Open chart (new tab)'
      }
      className={`inline-flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap rounded-md tabular-nums text-muted-foreground hover:text-primary-600 hover:underline focus-visible:text-primary-600 focus-visible:underline ${
        phone ? 'min-h-11 min-w-11 text-sm' : 'min-w-9 text-[11px]'
      }`}
    >
      <ClipboardList aria-hidden='true' className='h-3 w-3 shrink-0' />
      {card.mrn ? (
        <FittedText
          text={`MRN ${card.mrn}`}
          fit={fits => fitMiddle('MRN ', card.mrn ?? '', fits)}
        />
      ) : (
        <span className='truncate'>
          No MRN · <span className='text-primary-600'>Open chart</span>
        </span>
      )}
      <ExternalLink aria-hidden='true' className='h-2.5 w-2.5 shrink-0' />
      <span className='sr-only'>, opens the chart in a new tab</span>
    </a>
  );

  return (
    <section
      aria-label='Patient'
      className={`shrink-0 border-b pr-1 ${phone ? 'pl-4' : 'pl-3'}`}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <div
          className={`${row} text-muted-foreground ${phone ? 'min-h-11 text-sm' : 'h-7 text-[11px]'}`}
        >
          {link}
          <span aria-hidden='true' className='shrink-0'>
            ·
          </span>
          {options.length >= 2 ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size={phone ? 'lg' : 'sm'}
                  title='Switch episode'
                  className={`shrink-0 gap-1 font-normal text-muted-foreground [&_svg]:size-2.5 ${phone ? 'min-h-11 px-1 text-sm' : 'px-0.5 text-[11px]'}`}
                >
                  <span className='whitespace-nowrap'>{rangeText}</span>
                  <ChevronDown aria-hidden='true' className='shrink-0' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start' className={MOTION.reduceRadix}>
                <DropdownMenuLabel>Episodes</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={channelId}
                  onValueChange={value => {
                    if (value !== channelId) {
                      useStore.getState().replaceChatWindow(channelId, value);
                      // The menu and its trigger leave with the old window, and a
                      // read-only chat has no composer to take focus; without a
                      // home, Esc would find focus on the page.
                      requestAnimationFrame(() => {
                        const active = document.activeElement;
                        if (!active || active === document.body) {
                          document
                            .querySelector<HTMLElement>(
                              `[data-chat-window="${value}"]`
                            )
                            ?.focus();
                        }
                      });
                    }
                  }}
                >
                  {options.map(option => (
                    <DropdownMenuRadioItem
                      key={option.channel_id}
                      value={option.channel_id}
                    >
                      <span className='flex items-center gap-2 text-xs'>
                        <span className='tabular-nums'>
                          {formatEpisodeRange(
                            option.start_date,
                            option.end_date,
                            false
                          )}
                        </span>
                        {option.read_only && (
                          <span title='Read only'>
                            <Lock aria-hidden='true' className='h-3 w-3' />
                            <span className='sr-only'>Read only, </span>
                          </span>
                        )}
                        <span>{statusDisplayLabel(option.status)}</span>
                        {!option.read_only && option.unread_count > 0 && (
                          <span aria-label={`${option.unread_count} unread`}>
                            <UnreadPill count={option.unread_count} />
                          </span>
                        )}
                      </span>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <p className='px-2 py-1.5 text-[11px] text-muted-foreground'>
                  Closed episodes are read only.
                </p>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className='flex shrink-0 items-center gap-1.5'>
              {rangeText}
            </span>
          )}
          {status}
          <CollapsibleTrigger asChild>
            <Button
              variant='ghost'
              size={phone ? 'iconLg' : 'icon'}
              className='ml-auto shrink-0'
              aria-label={
                open ? 'Hide patient details' : 'Show patient details'
              }
              title={open ? 'Hide patient details' : 'Show patient details'}
            >
              <Info aria-hidden='true' />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <dl
            aria-label='Patient details'
            className={`pb-2 pr-2 ${wide ? 'grid grid-cols-2 gap-x-4' : ''} ${phone ? 'max-h-32 overflow-y-auto' : ''}`}
          >
            <div className={row}>
              <dt className={dtClass}>DOB</dt>
              <dd className={`${ddClass} tabular-nums`}>
                {formatDateToMMDDYYYY(card.date_of_birth)}
              </dd>
            </div>
            {card.primary_diagnosis_code && (
              <div className={row}>
                <dt className={dtClass}>Diagnosis</dt>
                <dd
                  className={ddClass}
                  title={`${card.primary_diagnosis_code} ${card.primary_diagnosis_description ?? ''}`.trim()}
                >
                  <span className='mr-1.5 rounded bg-muted px-1 font-mono text-xs'>
                    {card.primary_diagnosis_code}
                  </span>
                  {card.primary_diagnosis_description}
                </dd>
              </div>
            )}
            {caseManager && (
              <div className={row}>
                <dt className={dtClass}>Case manager</dt>
                <dd className={ddClass} title={caseManager}>
                  {caseManager}
                </dd>
              </div>
            )}
            <div className={row}>
              <dt className={dtClass}>Clinical manager</dt>
              <dd className={ddClass} title={clinicalManager}>
                {clinicalManager}
              </dd>
            </div>
            {card.payer_name && (
              <div className={row}>
                <dt className={dtClass}>Payer</dt>
                <dd className={ddClass} title={card.payer_name}>
                  {card.payer_name}
                </dd>
              </div>
            )}
            <div className={row}>
              <dt className={dtClass}>Status</dt>
              <dd className={ddClass}>
                {statusDisplayLabel(card.episode_status)}
              </dd>
            </div>
            {nextVisit && (
              <div className={row}>
                <dt className={dtClass}>Next visit</dt>
                <dd className={ddClass} title={nextVisit}>
                  {nextVisit}
                </dd>
              </div>
            )}
            {card.clinic_name && (
              <div className={row}>
                <dt className={dtClass}>Clinic</dt>
                <dd className={ddClass} title={card.clinic_name}>
                  {card.clinic_name}
                </dd>
              </div>
            )}
            {(careTeam.length > 0 || wide) && (
              <div className={row}>
                <dt className={dtClass}>Care team</dt>
                <dd className={`${ddClass} flex flex-wrap items-center gap-1`}>
                  {careTeam.map(code => (
                    <span
                      key={code}
                      title={disciplineName(code)}
                      className='rounded bg-muted px-1 text-xs'
                    >
                      <span aria-hidden='true'>{code}</span>
                      <span className='sr-only'>{disciplineName(code)}</span>
                    </span>
                  ))}
                  {wide && memberCount !== undefined && (
                    <Button
                      variant='link'
                      size='xs'
                      className='ml-auto whitespace-nowrap'
                      onClick={onShowMembers}
                    >
                      {memberCount === 1
                        ? '1 member'
                        : `${memberCount} members`}
                    </Button>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}
